/**
 * Drives the "- [ n ] +" controls of the format dialogs.
 *
 * The buttons and the input change the value locally right away; the new
 * value reaches the server through onCommit, debounced so that clicking "+"
 * three times sends one request with the final number instead of three. A
 * typed value commits as soon as the field is left or Enter is pressed.
 *
 * The bounds come from the input: 0 at the bottom, data-maxorderquantity at
 * the top (0 means unlimited). The buttons are disabled at the bounds.
 */
export interface QuantityControlOptions {
    /** Milliseconds to wait after the last click before committing. 0 commits at once. */
    debounceMs: number;
    /** Called with the final value. May return a promise; the control is marked busy until it settles. */
    onCommit: (input: HTMLInputElement, quantity: number) => Promise<void> | void;
    /** Called after every local change, before the commit. */
    onChange?: (input: HTMLInputElement, quantity: number) => void;
    /** Called once a commit has settled, i.e. its request came back (or failed). */
    onSettled?: (input: HTMLInputElement) => void;
}

export class QuantityControl {
    static INPUT_SELECTOR = '.eventgallery-cartquantity';
    static PLUS_SELECTOR = '.eventgallery-qtyplus';
    static MINUS_SELECTOR = '.eventgallery-qtyminus';
    static CONTROL_SELECTOR = '.qty-control';

    private root: ParentNode;
    private options: QuantityControlOptions;
    private inputs: HTMLInputElement[] = [];
    private timers: Map<HTMLInputElement, number> = new Map();
    private lastCommitted: Map<HTMLInputElement, number> = new Map();
    /** Inputs whose last commit has not come back from the server yet. */
    private inFlight: Set<HTMLInputElement> = new Set();

    constructor(root: ParentNode, options: QuantityControlOptions) {
        this.root = root;
        this.options = options;
        this.bindAll();
    }

    bindAll() {
        this.inputs = Array.from(this.root.querySelectorAll<HTMLInputElement>(QuantityControl.INPUT_SELECTOR));

        this.inputs.forEach((input) => {
            this.lastCommitted.set(input, this.getValue(input));

            input.addEventListener('change', () => {
                this.setValue(input, this.clamp(input, this.getValue(input)));
                this.commit(input);
            });

            input.addEventListener('keydown', (e: KeyboardEvent) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    this.setValue(input, this.clamp(input, this.getValue(input)));
                    this.commit(input);
                } else if (e.key === 'Escape') {
                    e.preventDefault();
                    e.stopPropagation();
                    this.setValue(input, this.lastCommitted.get(input) ?? 0);
                    this.cancel(input);
                }
            });

            this.refreshButtons(input);
        });

        this.root.querySelectorAll<HTMLElement>(QuantityControl.PLUS_SELECTOR).forEach((button) => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                const input = this.inputForButton(button);
                if (input) this.step(input, 1);
            });
        });

        this.root.querySelectorAll<HTMLElement>(QuantityControl.MINUS_SELECTOR).forEach((button) => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                const input = this.inputForButton(button);
                if (input) this.step(input, -1);
            });
        });
    }

    getInputs(): HTMLInputElement[] {
        return this.inputs;
    }

    getValue(input: HTMLInputElement): number {
        const value = parseInt(input.value);
        return isNaN(value) ? 0 : value;
    }

    getMax(input: HTMLInputElement): number {
        const max = parseInt(input.getAttribute('data-maxorderquantity') ?? '0');
        return isNaN(max) ? 0 : max;
    }

    /**
     * Sets a value which the server confirmed, e.g. after it clamped the
     * quantity. A value the visitor is still changing, or one whose request
     * is still on its way, is left alone: the answer to an earlier request
     * does not know about it yet.
     */
    syncValue(input: HTMLInputElement, quantity: number) {
        if (this.timers.has(input) || this.inFlight.has(input)) {
            return;
        }
        this.lastCommitted.set(input, quantity);
        this.setValue(input, quantity);
    }

    /**
     * Commits every pending change right now. Called before the dialog goes
     * away, so nothing is lost inside the debounce window.
     */
    flushAll() {
        Array.from(this.timers.keys()).forEach((input) => this.commit(input));
    }

    hasPending(): boolean {
        return this.timers.size > 0;
    }

    refreshButtons(input: HTMLInputElement) {
        const value = this.getValue(input);
        const max = this.getMax(input);
        const buttons = this.buttonsFor(input);

        // a format which cannot be applied stays disabled as a whole
        if (input.disabled) {
            if (buttons.plus) buttons.plus.disabled = true;
            if (buttons.minus) buttons.minus.disabled = true;
            return;
        }

        if (buttons.plus) {
            const atMax = max > 0 && value >= max;
            buttons.plus.disabled = atMax;
            buttons.plus.title = atMax ? (input.getAttribute('data-max-hint') ?? '') : '';
        }
        if (buttons.minus) {
            buttons.minus.disabled = value <= 0;
        }
    }

    private step(input: HTMLInputElement, delta: number) {
        this.setValue(input, this.clamp(input, this.getValue(input) + delta));
        this.schedule(input);
    }

    private clamp(input: HTMLInputElement, value: number): number {
        const max = this.getMax(input);
        value = Math.max(0, value);
        return max > 0 ? Math.min(max, value) : value;
    }

    private setValue(input: HTMLInputElement, value: number) {
        input.value = String(value);
        this.refreshButtons(input);
        if (this.options.onChange) {
            this.options.onChange(input, value);
        }
    }

    private schedule(input: HTMLInputElement) {
        this.cancel(input);
        if (this.options.debounceMs <= 0) {
            this.commit(input);
            return;
        }
        this.timers.set(input, window.setTimeout(() => this.commit(input), this.options.debounceMs));
    }

    private cancel(input: HTMLInputElement) {
        const timer = this.timers.get(input);
        if (timer !== undefined) {
            window.clearTimeout(timer);
            this.timers.delete(input);
        }
    }

    private commit(input: HTMLInputElement) {
        this.cancel(input);

        const quantity = this.getValue(input);
        if (quantity === this.lastCommitted.get(input)) {
            return;
        }
        this.lastCommitted.set(input, quantity);

        const control = input.closest(QuantityControl.CONTROL_SELECTOR);
        if (control) control.classList.add('is-busy');
        this.inFlight.add(input);
        const done = () => {
            this.inFlight.delete(input);
            if (control) control.classList.remove('is-busy');
            if (this.options.onSettled) {
                this.options.onSettled(input);
            }
        };

        const result = this.options.onCommit(input, quantity);
        if (result && typeof (result as Promise<void>).then === 'function') {
            (result as Promise<void>).then(done, done);
        } else {
            done();
        }
    }

    private inputForButton(button: HTMLElement): HTMLInputElement | null {
        const name = button.getAttribute('field');
        if (!name) {
            return null;
        }
        return this.inputs.find((input) => input.name === name) ?? null;
    }

    private buttonsFor(input: HTMLInputElement): {plus: HTMLButtonElement | null, minus: HTMLButtonElement | null} {
        const name = input.name;
        const find = (selector: string): HTMLButtonElement | null => {
            const buttons = this.root.querySelectorAll<HTMLButtonElement>(selector);
            for (let i = 0; i < buttons.length; i++) {
                if (buttons[i].getAttribute('field') === name) return buttons[i];
            }
            return null;
        };
        return {plus: find(QuantityControl.PLUS_SELECTOR), minus: find(QuantityControl.MINUS_SELECTOR)};
    }
}
